import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { useBrandName } from "@/api/hooks/tenant";
import { Logo } from "@/components/common/logo";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { cn } from "@/lib/utils";

import { useHomePath } from "./use-home-path";

interface StatusMessageProps {
  icon: LucideIcon;
  /** Small label above the title, e.g. "404". */
  eyebrow?: string;
  /** Page heading; also used as the document title. */
  title: string;
  description: ReactNode;
  actions: ReactNode;
  /** Extra content below the actions (e.g. error details). */
  children?: ReactNode;
}

/** Centered status message for not-found and error screens. */
export function StatusMessage({ icon: Icon, eyebrow, title, description, actions, children }: StatusMessageProps) {
  useDocumentTitle(title);

  return (
    <div className="mx-auto flex w-full max-w-md flex-col items-center text-center">
      <div className="flex size-12 items-center justify-center rounded-xl border bg-card text-muted-foreground">
        <Icon className="size-5" aria-hidden />
      </div>
      {eyebrow && <p className="mt-6 font-mono text-xs font-medium tracking-widest text-muted-foreground">{eyebrow}</p>}
      <h1 className={cn("text-2xl font-semibold tracking-tight text-balance", eyebrow ? "mt-2" : "mt-6")}>{title}</h1>
      <p className="mt-2 text-[15px] text-balance text-muted-foreground">{description}</p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-2">{actions}</div>
      {children}
    </div>
  );
}

/** Full-viewport frame (logo + centered content) for screens rendered outside the app shell. */
export function FullPageStatus({ children }: { children: ReactNode }) {
  const brand = useBrandName();
  const homePath = useHomePath();

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="px-4 py-5 sm:px-8">
        <Link
          to={homePath}
          aria-label={`${brand} home`}
          className="inline-flex rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40"
        >
          <Logo />
        </Link>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 pt-8 pb-24 sm:px-8">{children}</main>
    </div>
  );
}
